import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import APIGateway from '@/api';
import { OrgMember } from '@/types/auth';
import { FGA_TO_AUTH0_ROLE, ROLE_TO_FGA, SelectableFgaRelation } from '@/types/fga';
import { User } from '@/types/user';


export const orgKeys = {
  all: [ 'org' ] as const,
  users: () => [ ...orgKeys.all, 'users' ] as const,
  user: ( userId: string ) => [ ...orgKeys.all, 'user', userId ] as const,
  invitations: () => [ ...orgKeys.all, 'invitations' ] as const,
  roles: () => [ ...orgKeys.all, 'roles' ] as const
};

export function useOrgUsersQuery() {
  return useQuery( {
    queryKey: orgKeys.users(),
    queryFn: async() => {
      const { users } = await APIGateway.users.usersGet();
      return users;
    },
    structuralSharing: false
  } );
}

export function useUserQuery( userId: string | undefined ) {
  return useQuery( {
    queryKey: orgKeys.user( userId ?? '' ),
    queryFn: async() => {
      if ( !userId ) throw new Error( 'User ID required' );
      const { user } = await APIGateway.users.userGet( userId );
      return user;
    },
    enabled: !!userId
  } );
}

export function useOrgUsersMapQuery() {
  return useQuery( {
    queryKey: orgKeys.users(),
    queryFn: async() => {
      const { users } = await APIGateway.users.usersGet();

      const mappedUsers = new Map<string, OrgMember>();
      for ( const user of users ) {
        mappedUsers.set( user.id, {
          ...user,
          userId: user.id
        } );
      }

      return mappedUsers;
    },
    structuralSharing: false
  } );
}

export function useOrgInvitationsQuery() {
  return useQuery( {
    queryKey: orgKeys.invitations(),
    queryFn: async() => {
      const { invitations } = await APIGateway.auth.orgInvitesGet();
      return invitations;
    }
  } );
}

export function useOrgRolesQuery() {
  return useQuery( {
    queryKey: orgKeys.roles(),
    queryFn: async() => {
      const { roles } = await APIGateway.auth.orgRolesGet();

      return roles;
    }
  } );
}

export function useInviteUserMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { email }: { email: string } ) => {
      const result = await APIGateway.auth.orgUserInvite( { email } );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: orgKeys.invitations() } );
    }
  } );
}

export function useSetUserRoleMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: ( { userId, role }: { userId: string; role: SelectableFgaRelation } ) =>
      APIGateway.users.userRoleSet( userId, role ),
    onMutate: async( { userId, role } ) => {
      await queryClient.cancelQueries( { queryKey: orgKeys.users() } );
      const previousUsers = queryClient.getQueryData( orgKeys.users() );
      queryClient.setQueryData<User[]>( orgKeys.users(), ( old ) =>
        old?.map( ( user ) => {
          if ( user.id !== userId ) return user;
          const roles = user.roles.filter( ( r ) => !( r.name in ROLE_TO_FGA ) );
          const auth0RoleName = FGA_TO_AUTH0_ROLE[ role ];
          if ( auth0RoleName ) {
            roles.push( { id: '', name: auth0RoleName } );
          }
          return { ...user, roles };
        } )
      );
      return { previousUsers };
    },
    onError: ( _, __, context ) => {
      if ( context?.previousUsers ) {
        queryClient.setQueryData( orgKeys.users(), context.previousUsers );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries( { queryKey: orgKeys.users() } );
    }
  } );
}

export function useDeleteInvitationMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { invitationId }: { invitationId: string } ) => {
      const result = await APIGateway.auth.orgInviteDelete( invitationId );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: orgKeys.invitations() } );
    }
  } );
}

export function useDeleteUserMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { userId }: { userId: string } ) => {
      const result = await APIGateway.users.userDelete( userId );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: orgKeys.users() } );
    }
  } );
}

export function useBlockUserMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { userId }: { userId: string } ) => {
      const result = await APIGateway.users.userBlock( userId );
      return result;
    },
    // Optimistic update, as there seem to be some delays on the auth0 side when blocking/unblocking users
    onMutate: async( { userId } ) => {
      await queryClient.cancelQueries( { queryKey: orgKeys.users() } );
      const previousUsers = queryClient.getQueryData( orgKeys.users() );
      queryClient.setQueryData<User[]>( orgKeys.users(), ( old ) => {
        if ( !old ) return old;
        return old.map( ( user ) => ( user.id === userId ? { ...user, blocked: true } : user )
        );
      } );

      return { previousUsers };
    },
    onError: ( _, __, context ) => {
      if ( context?.previousUsers ) {
        queryClient.setQueryData( orgKeys.users(), context.previousUsers );
      }
    }
  } );
}

export function useUnblockUserMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { userId }: { userId: string } ) => {
      const result = await APIGateway.users.userUnblock( userId );
      return result;
    },
    onMutate: async( { userId } ) => {
      await queryClient.cancelQueries( { queryKey: orgKeys.users() } );
      const previousUsers = queryClient.getQueryData( orgKeys.users() );
      queryClient.setQueryData<User[]>( orgKeys.users(), ( old ) => {
        if ( !old ) return old;
        return old.map( ( user ) => ( user.id === userId ? { ...user, blocked: false } : user )
        );
      } );

      return { previousUsers };
    },
    onError: ( _, __, context ) => {
      if ( context?.previousUsers ) {
        queryClient.setQueryData( orgKeys.users(), context.previousUsers );
      }
    }
  } );
}
